import React, { forwardRef } from "react";
import styled, { css } from "styled-components";

const StyledButton = styled.button<IButtonStyledProps>`
  display: block;
  padding: 1.5rem ${(props) => props.theme.space[3]};
  border: 0;
  background-color: ${(props) => props.theme.colors.secondary};
  box-shadow: inset 0 0 0 2px ${({ theme }) => theme.colors.secondary};
  font-family: ${(props) => props.theme.fonts.body};
  font-size: ${(props) => props.theme.fontSizes[1]};
  font-weight: 500;
  border-radius: ${(props) => props.theme.radii[1]};
  color: white;
  text-align: center;
  line-height: 1;
  cursor: pointer;

  ${(props) =>
    props.variant === "secondary" &&
    css`
      background-color: ${(props) => props.theme.colors.white};
      box-shadow: inset 0 0 0 2px ${({ theme }) => theme.colors.secondary};
      color: ${(props) => props.theme.colors.secondary};
    `}

  ${(props) =>
    props.disabled &&
    css`
      box-shadow: inset 0 0 0 1px ${({ theme }) => theme.colors.offBlack};
      background-color: ${(props) => `${props.theme.colors.greyLight}`};
      color: ${(props) => props.theme.colors.offBlack};
      cursor: default;
      &:hover {
        cursor: not-allowed;
      }
    `}

  ${(props) =>
    props.fullwidth &&
    css`
      width: 100%;
    `};
`;

export interface IButtonStyledProps {
  /**
   * Is the button disabled?
   */
  disabled?: boolean;
  /**
   * Should the button be the full width of the container?
   */
  fullwidth?: "true" | "";
  /**
   * Should the button be the full width of the container?
   */
  minwidth?: "true" | "";
  /**
   * Set visual prominence of the button
   */
  variant?: "primary" | "secondary";
}

interface IProps {
  /**
   * Optional click handler
   */
  onClick?: () => void;
  /** Specifiy the button type */
  type?: "button" | "submit" | "reset";
  children: React.ReactNode;
}

type Props = IButtonStyledProps & IProps;

const Button = forwardRef<HTMLButtonElement, Props>(
  (
    {
      children,
      disabled,
      fullwidth = "",
      minwidth = "",
      type = "button",
      variant = "primary",
      ...rest
    },
    ref,
  ) => (
    <StyledButton
      ref={ref}
      type={type}
      fullwidth={fullwidth}
      minwidth={minwidth}
      variant={variant}
      disabled={disabled}
      {...rest}
    >
      {children}
    </StyledButton>
  ),
);

export default Button;
